import type { CommunityShareMethod } from './profile'
import type { CommunityLibraryTab } from './pageViews'
import type { PostHogContextRef } from '../context'

/**
 * Playlist creation method.
 */
export type CommunityPlaylistCreationMethod = 'empty' | 'smart_create'

/**
 * For `community_playlist_created` with `method === 'smart_create'`: how the
 * user arrived at the seed prompt that drove the alchemize generation.
 *
 *  - `typed`      — free-form text entered in the ChatInput on the smart-seed
 *                   branch screen.
 *  - `suggestion` — exact match against a server-curated pill returned by
 *                   `featureSeedQuestionList`. The pill set is rotated per
 *                   `network-only` fetch, so this signal differentiates
 *                   "browsed our suggestions" from "thought of something".
 *  - `wizard`     — user opted into the mood + time + interests flow. Currently
 *                   unreachable from the UI (entry button is commented out)
 *                   but kept in the union so re-enabling the wizard does not
 *                   require a schema change.
 */
export type CommunityPlaylistSmartCreateSource =
  | 'typed'
  | 'suggestion'
  | 'wizard'

/**
 * Optional structured details captured at save time for
 * `community_playlist_created`.
 *
 * Empty-path saves only populate `was_auto_renamed`. Smart-create saves
 * populate the rest. All fields are optional — the factory only emits
 * present keys onto the payload, so PostHog dashboards see consistent
 * snake_case columns without nulls for the irrelevant path.
 *
 * Intentionally absent: the raw seed prompt text. The text is free-form
 * user input and may contain PII; we capture `prompt_source` as a
 * discriminator instead. This matches the broader convention in this
 * file of capturing derived attributes, not raw user content / UUIDs.
 */
export type CommunityPlaylistCreatedDetails = {
  items_count?: number
  target_duration_minutes?: number | null
  has_user_edited_name?: boolean
  narrative_present?: boolean
  prompt_source?: CommunityPlaylistSmartCreateSource
  was_auto_renamed?: boolean
}

/**
 * Entry-point context for `community_playlist_item_played` — answers "where
 * did this playback originate from?". The event is scoped to plays from
 * playlists curated by *others* (i.e. discovered or followed), so currently
 * only `'discover'` and `'following'` have call sites; the other values stay
 * in the union for future expansion (a shared-link / profile / carousel /
 * library play would slot in without a type change).
 */
export type CommunityPlaylistItemEntryPoint =
  | 'shared_link'
  | 'library'
  | 'profile'
  | 'discover'
  | 'carousel'
  | 'following'

/**
 * Viewer's relationship to a playlist they're playing from — for the
 * `community_playlist_item_played.ownership` breakdown.
 */
export type CommunityPlaylistOwnership = 'own' | 'followed' | 'other'

/**
 * Community playlist & My Library action events.
 *
 * `community_playlist_shared` carries the playlist *title* (not its id) to keep
 * shared/viral surfaces title-only and dashboard-readable.
 *
 * `community_playlist_item_played` is the *consumption* signal — fires when a
 * video launches from a Community playlist context. `entry_point` says where
 * the play came from (shared link, user's own library, Discover, etc.).
 * `content_title` is the human-readable name of the played video (mirrors the
 * tile's `tile-v2__bottom-metadata-title`). `creator_display_name` attributes
 * the play back to the playlist's author by their public-facing display name
 * — NO creator UUID is captured (the logged-in user's identity comes from
 * PostHog `identify()` person properties; third-party creator UUIDs are
 * intentionally omitted for privacy).
 */
export type PlaylistLibraryEvents = {
  community_playlist_created: (
    method: CommunityPlaylistCreationMethod,
    details?: CommunityPlaylistCreatedDetails,
  ) => void
  community_playlist_deleted: (playlistId: string) => void
  /**
   * Fires when the user clicks the share icon to *open* the share modal —
   * intent signal, NOT completion. Pair with `community_playlist_shared`
   * (which fires only when the user clicks Copy/Native inside the modal) to
   * compute share-modal abandonment rate.
   */
  community_playlist_share_initiated: (playlistTitle: string) => void
  community_playlist_shared: (playlistTitle: string, method: CommunityShareMethod) => void
  community_playlist_followed: (playlistId: string) => void
  community_playlist_unfollowed: (playlistId: string) => void
  community_library_tab_changed: (tab: CommunityLibraryTab) => void
  community_playlist_item_played: (
    contentId: string,
    entryPoint: CommunityPlaylistItemEntryPoint,
    playlistId?: string,
    position?: number,
    ownership?: CommunityPlaylistOwnership,
    viewerIsMember?: boolean,
    contentTitle?: string,
    creatorDisplayName?: string,
  ) => void
}

export function createPlaylistLibraryEventCallbacks (
  contextRef: PostHogContextRef,
): PlaylistLibraryEvents {
  return {
    community_playlist_created: (
      method: CommunityPlaylistCreationMethod,
      details?: CommunityPlaylistCreatedDetails,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_created', {
        context: 'community',
        method,
        ...(details?.items_count != null && { items_count: details.items_count }),
        ...(details?.target_duration_minutes !== undefined
          && { target_duration_minutes: details.target_duration_minutes }),
        ...(details?.has_user_edited_name != null
          && { has_user_edited_name: details.has_user_edited_name }),
        ...(details?.narrative_present != null
          && { narrative_present: details.narrative_present }),
        ...(details?.prompt_source && { prompt_source: details.prompt_source }),
        ...(details?.was_auto_renamed != null
          && { was_auto_renamed: details.was_auto_renamed }),
      })
    },

    community_playlist_deleted: (playlistId: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_deleted', {
        context: 'community',
        playlist_id: playlistId,
      })
    },

    community_playlist_share_initiated: (playlistTitle: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_share_initiated', {
        context: 'community',
        playlist_title: playlistTitle,
      })
    },

    community_playlist_shared: (playlistTitle: string, method: CommunityShareMethod) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_shared', {
        context: 'community',
        playlist_title: playlistTitle,
        method,
      })
    },

    community_playlist_followed: (playlistId: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_followed', {
        context: 'community',
        playlist_id: playlistId,
      })
    },

    community_playlist_unfollowed: (playlistId: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_unfollowed', {
        context: 'community',
        playlist_id: playlistId,
      })
    },

    community_library_tab_changed: (tab: CommunityLibraryTab) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_library_tab_changed', {
        context: 'community',
        tab,
      })
    },

    community_playlist_item_played: (
      playedContentId: string,
      entryPoint: CommunityPlaylistItemEntryPoint,
      playlistId?: string,
      position?: number,
      ownership?: CommunityPlaylistOwnership,
      viewerIsMember?: boolean,
      contentTitle?: string,
      creatorDisplayName?: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_item_played', {
        context: 'community',
        content_id: playedContentId,
        entry_point: entryPoint,
        ...(playlistId && { playlist_id: playlistId }),
        ...(position != null && { position }),
        ...(ownership && { ownership }),
        ...(viewerIsMember != null && { viewer_is_member: viewerIsMember }),
        ...(contentTitle && { content_title: contentTitle }),
        ...(creatorDisplayName && { creator_display_name: creatorDisplayName }),
      })
    },

  }
}
